import { useEffect, useState } from "react";
import {
  X,
  Upload,
  Loader2,
  Save,
  Image as ImageIcon,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

import seoService from "@/services/seoService";

const PAGE_LIST = [
  { value: "home", label: "Home", path: "/" },
  {
    value: "about",
    label: "About",
    path: "/about",
  },
  {
    value: "exam-pattern-and-syllabus",
    label: "Exam Pattern And Syllabus",
    path: "/exam-pattern-and-syllabus",
  },
  {
    value: "blog",
    label: "blog",
    path: "/blog",
  },
  {
    value: "forgot-password",
    label: "forgot password",
    path: "/forgot-password",
  },
  {
    value: "program",
    label: "Program",
    path: "/program",
  },
  {
    value: "dubai-experience",
    label: "Dubai Experience",
    path: "/dubai-experience",
  },
  {
    value: "faqs",
    label: "FAQs",
    path: "/faqs",
  },
  {
    value: "contact",
    label: "Contact",
    path: "/contact",
  },
  {
    value: "privacy-policy",
    label: "Privacy Policy",
    path: "/privacy-policy",
  },
  {
    value: "refund-policy",
    label: "Refund Policy",
    path: "/refund-policy",
  },
  {
    value: "terms-conditions",
    label: "Terms & Conditions",
    path: "/terms-conditions",
  },
  {
    value: "login",
    label: "Candidate Login",
    path: "/login",
  },
  {
    value: "apply",
    label: "Apply",
    path: "/apply",
  },
  {
    value: "dashboard",
    label: "Candidate Dashboard",
    path: "/dashboard",
  },
  {
    value: "admin-dashboard",
    label: "Admin Dashboard",
    path: "/admin/dashboard",
  },
];

const EMPTY_FORM = {
  page: "",
  metaTitle: "",
  metaDescription: "",
  focusKeywords: "",
  metaKeywords: "",
  canonicalUrl: "",
  robots: "index, follow",

  ogTitle: "",
  ogDescription: "",

  twitterTitle: "",
  twitterDescription: "",
  twitterCard: "summary_large_image",

  schema: "",
  faqSchema: "",

  isActive: true,
};

const DEFAULT_IMAGE =
  "https://corporateentrancetest.com/og-image.png";

export default function SEOForm({
  seo,
  onClose,
  onSaved,
}) {
  const isEdit = Boolean(seo);

  const [form, setForm] =
    useState(EMPTY_FORM);

  const [ogImage, setOgImage] =
    useState(null);

  const [twitterImage, setTwitterImage] =
    useState(null);

  const [ogPreview, setOgPreview] =
    useState(DEFAULT_IMAGE);

  const [twitterPreview, setTwitterPreview] =
    useState(DEFAULT_IMAGE);

    const [removeOgImage, setRemoveOgImage] =
  useState(false);

const [removeTwitterImage, setRemoveTwitterImage] =
  useState(false);

  const [saving, setSaving] =
    useState(false);

  // ==========================================
  // LOAD SEO
  // ==========================================

  useEffect(() => {
    if (!seo) {
      setForm(EMPTY_FORM);
      setOgImage(null);
      setTwitterImage(null);
      setRemoveOgImage(false);
      setRemoveTwitterImage(false);
      setOgPreview(DEFAULT_IMAGE);
      setTwitterPreview(DEFAULT_IMAGE);
      return;
    }

    setForm({
      page: seo.page || "",

      metaTitle: seo.metaTitle || "",
      metaDescription:
        seo.metaDescription || "",

      focusKeywords:
        Array.isArray(seo.focusKeywords)
          ? seo.focusKeywords.join(", ")
          : "",

      metaKeywords:
        Array.isArray(seo.metaKeywords)
          ? seo.metaKeywords.join(", ")
          : "",

      canonicalUrl:
        seo.canonicalUrl || "",

      robots:
        seo.robots || "index, follow",

      ogTitle: seo.ogTitle || "",
      ogDescription:
        seo.ogDescription || "",

      twitterTitle:
        seo.twitterTitle || "",

      twitterDescription:
        seo.twitterDescription || "",

      twitterCard:
        seo.twitterCard ||
        "summary_large_image",

      schema: seo.schema
        ? JSON.stringify(
            seo.schema,
            null,
            2
          )
        : "",

      faqSchema: seo.faqSchema
        ? JSON.stringify(
            seo.faqSchema,
            null,
            2
          )
        : "",

      isActive:
        seo.isActive !== undefined
          ? seo.isActive
          : true,
    });

    setOgPreview(
      seo.ogImage || DEFAULT_IMAGE
    );

    setTwitterPreview(
      seo.twitterImage ||
        DEFAULT_IMAGE
    );

    setOgImage(null);
    setTwitterImage(null);
  }, [seo]);

  // ==========================================
  // CHANGE
  // ==========================================

  function handleChange(e) {
    const {
      name,
      value,
      type,
      checked,
    } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : value,
    }));
  }

  // ==========================================
  // OG IMAGE
  // ==========================================

  function handleOgImage(e) {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error(
        "Please select a valid image"
      );
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error(
        "OG image must be less than 5MB"
      );
      return;
    }

    setOgImage(file);

    setOgPreview(
      URL.createObjectURL(file)
    );
  }

  function handleRemoveOgImage() {
  if (ogPreview?.startsWith("blob:")) {
    URL.revokeObjectURL(ogPreview);
  }

  setOgImage(null);
  setOgPreview(DEFAULT_IMAGE);

  if (isEdit) {
    setRemoveOgImage(true);
  }
}

  // ==========================================
  // TWITTER IMAGE
  // ==========================================

  function handleTwitterImage(e) {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error(
        "Please select a valid image"
      );
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error(
        "Twitter image must be less than 5MB"
      );
      return;
    }

    setTwitterImage(file);

    setTwitterPreview(
      URL.createObjectURL(file)
    );
  }

  function handleRemoveTwitterImage() {
  if (twitterPreview?.startsWith("blob:")) {
    URL.revokeObjectURL(twitterPreview);
  }

  setTwitterImage(null);
  setTwitterPreview(DEFAULT_IMAGE);

  if (isEdit) {
    setRemoveTwitterImage(true);
  }
}

  // ==========================================
  // JSON VALIDATION
  // ==========================================

  function validateJSON(value, label) {
    if (!value.trim()) return true;

    try {
      JSON.parse(value);
      return true;
    } catch {
      toast.error(
        `${label} contains invalid JSON`
      );
      return false;
    }
  }

  // ==========================================
  // SUBMIT
  // ==========================================

  async function handleSubmit(e) {
    e.preventDefault();

    if (!form.page) {
      toast.error("Please select a page");
      return;
    }

    if (!form.metaTitle.trim()) {
      toast.error("Meta title is required");
      return;
    }

    if (!form.metaDescription.trim()) {
      toast.error(
        "Meta description is required"
      );
      return;
    }

    if (
      !validateJSON(
        form.schema,
        "JSON-LD Schema"
      )
    ) {
      return;
    }

    if (
      !validateJSON(
        form.faqSchema,
        "FAQ Schema"
      )
    ) {
      return;
    }

    try {
      setSaving(true);

      const formData = new FormData();

      formData.append(
        "page",
        form.page
      );

      formData.append(
        "metaTitle",
        form.metaTitle.trim()
      );

      formData.append(
        "metaDescription",
        form.metaDescription.trim()
      );

      formData.append(
        "focusKeywords",
        form.focusKeywords
      );

      formData.append(
        "metaKeywords",
        form.metaKeywords
      );

      formData.append(
        "canonicalUrl",
        form.canonicalUrl.trim()
      );

      formData.append(
        "robots",
        form.robots
      );

      formData.append(
        "ogTitle",
        form.ogTitle.trim()
      );

      formData.append(
        "ogDescription",
        form.ogDescription.trim()
      );

      formData.append(
        "twitterTitle",
        form.twitterTitle.trim()
      );

      formData.append(
        "twitterDescription",
        form.twitterDescription.trim()
      );

      formData.append(
        "twitterCard",
        form.twitterCard
      );

      if (form.schema.trim()) {
        formData.append(
          "schema",
          form.schema
        );
      }

      if (form.faqSchema.trim()) {
        formData.append(
          "faqSchema",
          form.faqSchema
        );
      }

      formData.append(
        "isActive",
        String(form.isActive)
      );

      if (ogImage) {
        formData.append(
          "ogImage",
          ogImage
        );
      }

      if (twitterImage) {
        formData.append(
          "twitterImage",
          twitterImage
        );
      }

      if (isEdit && removeOgImage) {
  formData.append(
    "removeOgImage",
    "true"
  );
}

if (isEdit && removeTwitterImage) {
  formData.append(
    "removeTwitterImage",
    "true"
  );
}

      let response;

      if (isEdit) {
        response =
          await seoService.update(
            seo.page,
            formData
          );
      } else {
        response =
          await seoService.create(
            formData
          );
      }

      toast.success(
        response?.message ||
          (isEdit
            ? "SEO updated successfully"
            : "SEO created successfully")
      );

      onSaved();
    } catch (error) {
      console.error(
        "SEO SAVE ERROR:",
        error
      );

      toast.error(
        error?.response?.data?.message ||
          "Failed to save SEO"
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-5">
      {/* BACKDROP */}

      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={() => {
          if (!saving) onClose();
        }}
      />

      {/* MODAL */}

      <div className="relative z-10 flex w-full max-w-5xl max-h-[94vh] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
        {/* HEADER */}

        <div className="flex items-center justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="font-heading text-xl sm:text-2xl font-bold text-slate-900">
              {isEdit
                ? "Update SEO"
                : "Add SEO"}
            </h2>

            <p className="mt-1 text-sm text-slate-500">
              Manage meta tags, social tags,
              structured data and FAQ schema.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* FORM */}

        <form
          onSubmit={handleSubmit}
          className="overflow-y-auto p-4 sm:p-6 space-y-7"
        >
          {/* PAGE SETTINGS */}

          <Section title="Page Settings">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Page" required>
                <select
                  name="page"
                  value={form.page}
                  onChange={handleChange}
                  disabled={isEdit}
                  className="seo-input"
                >
                  <option value="">
                    Select Page
                  </option>

                  {PAGE_LIST.map((page) => (
                    <option
                      key={page.value}
                      value={page.value}
                    >
                      {page.label} —{" "}
                      {page.path}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Robots">
                <select
                  name="robots"
                  value={form.robots}
                  onChange={handleChange}
                  className="seo-input"
                >
                  <option value="index, follow">
                    index, follow
                  </option>

                  <option value="noindex, follow">
                    noindex, follow
                  </option>

                  <option value="index, nofollow">
                    index, nofollow
                  </option>

                  <option value="noindex, nofollow">
                    noindex, nofollow
                  </option>
                </select>
              </Field>
            </div>

            <label className="mt-4 flex cursor-pointer items-center gap-3">
              <input
                type="checkbox"
                name="isActive"
                checked={form.isActive}
                onChange={handleChange}
                className="h-4 w-4 accent-[#F5A623]"
              />

              <span className="text-sm text-slate-600">
                Enable SEO for this page
              </span>
            </label>
          </Section>

          {/* BASIC SEO */}

          <Section title="Basic SEO">
            <div className="space-y-4">
              <Field
                label="Meta Title"
                required
                count={form.metaTitle.length}
                max={70}
              >
                <input
                  type="text"
                  name="metaTitle"
                  value={form.metaTitle}
                  onChange={handleChange}
                  maxLength={70}
                  placeholder="Enter SEO meta title"
                  className="seo-input"
                />
              </Field>

              <Field
                label="Meta Description"
                required
                count={
                  form.metaDescription.length
                }
                max={170}
              >
                <textarea
                  name="metaDescription"
                  value={form.metaDescription}
                  onChange={handleChange}
                  maxLength={170}
                  rows={4}
                  placeholder="Enter SEO meta description"
                  className="seo-input resize-none"
                />
              </Field>

              <Field label="Focus Keywords">
                <input
                  type="text"
                  name="focusKeywords"
                  value={form.focusKeywords}
                  onChange={handleChange}
                  placeholder="corporate entrance test, CET exam, entrance test"
                  className="seo-input"
                />

                <p className="mt-1 text-xs text-slate-400">
                  Separate keywords with commas.
                </p>
              </Field>

              <Field label="Meta Keywords">
                <input
                  type="text"
                  name="metaKeywords"
                  value={form.metaKeywords}
                  onChange={handleChange}
                  placeholder="cet exam, corporate exam"
                  className="seo-input"
                />

                <p className="mt-1 text-xs text-slate-400">
                  Kept for compatibility with
                  legacy SEO systems.
                </p>
              </Field>

              <Field label="Canonical URL">
                <input
                  type="url"
                  name="canonicalUrl"
                  value={form.canonicalUrl}
                  onChange={handleChange}
                  placeholder="https://corporateentrancetest.com/about"
                  className="seo-input"
                />
              </Field>
            </div>
          </Section>

          {/* OPEN GRAPH */}

          <Section title="Open Graph">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field
                label="OG Title"
                count={form.ogTitle.length}
                max={70}
              >
                <input
                  type="text"
                  name="ogTitle"
                  value={form.ogTitle}
                  onChange={handleChange}
                  maxLength={70}
                  placeholder="Open Graph title"
                  className="seo-input"
                />
              </Field>

              <Field label="OG Description">
                <textarea
                  name="ogDescription"
                  value={form.ogDescription}
                  onChange={handleChange}
                  maxLength={200}
                  rows={3}
                  placeholder="Open Graph description"
                  className="seo-input resize-none"
                />
              </Field>
            </div>

         <ImageUpload
  label="OG Image"
  file={ogImage}
  preview={ogPreview}
  onChange={handleOgImage}
  onRemove={handleRemoveOgImage}
/>
          </Section>

          {/* TWITTER */}

          <Section title="Twitter / X">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field
                label="Twitter Title"
                count={
                  form.twitterTitle.length
                }
                max={70}
              >
                <input
                  type="text"
                  name="twitterTitle"
                  value={form.twitterTitle}
                  onChange={handleChange}
                  maxLength={70}
                  placeholder="Twitter title"
                  className="seo-input"
                />
              </Field>

              <Field label="Twitter Card">
                <select
                  name="twitterCard"
                  value={form.twitterCard}
                  onChange={handleChange}
                  className="seo-input"
                >
                  <option value="summary_large_image">
                    Summary Large Image
                  </option>

                  <option value="summary">
                    Summary
                  </option>
                </select>
              </Field>
            </div>

            <div className="mt-4">
              <Field label="Twitter Description">
                <textarea
                  name="twitterDescription"
                  value={
                    form.twitterDescription
                  }
                  onChange={handleChange}
                  maxLength={200}
                  rows={3}
                  placeholder="Twitter description"
                  className="seo-input resize-none"
                />
              </Field>
            </div>

      <ImageUpload
  label="Twitter Image"
  file={twitterImage}
  preview={twitterPreview}
  onChange={handleTwitterImage}
  onRemove={handleRemoveTwitterImage}
/>
          </Section>

          {/* JSON LD */}

          <Section title="Structured Data — JSON-LD">
            <InfoBox
              icon={AlertCircle}
              text="Enter valid JSON-LD structured data. JSON will be validated before saving."
            />

            <Field label="JSON-LD Schema">
              <textarea
                name="schema"
                value={form.schema}
                onChange={handleChange}
                rows={12}
                spellCheck="false"
                placeholder={`{
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Corporate Entrance Test",
  "url": "https://corporateentrancetest.com/"
}`}
                className="seo-input resize-y font-mono text-xs leading-5"
              />
            </Field>
          </Section>

          {/* FAQ */}

          <Section title="FAQ Schema">
            <InfoBox
              icon={AlertCircle}
              text="Add FAQ structured data only when the questions and answers are actually visible and relevant on the page."
            />

            <Field label="FAQ JSON-LD">
              <textarea
                name="faqSchema"
                value={form.faqSchema}
                onChange={handleChange}
                rows={12}
                spellCheck="false"
                placeholder={`{
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is the Corporate Entrance Test?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The Corporate Entrance Test is..."
      }
    }
  ]
}`}
                className="seo-input resize-y font-mono text-xs leading-5"
              />
            </Field>
          </Section>

          {/* FOOTER */}

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 border-t border-slate-200 pt-5">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-lg border border-slate-300 px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#F5A623] px-5 py-2.5 text-sm font-bold text-slate-950 hover:bg-[#ffb638] disabled:opacity-50"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  {isEdit
                    ? "Update SEO"
                    : "Save SEO"}
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <style>{`
        .seo-input {
          width: 100%;
          background: white;
          border: 1px solid #cbd5e1;
          border-radius: 0.5rem;
          padding: 0.65rem 0.75rem;
          color: #0f172a;
          outline: none;
          transition: 0.2s;
        }

        .seo-input:focus {
          border-color: #F5A623;
          box-shadow: 0 0 0 1px #F5A623;
        }

        .seo-input::placeholder {
          color: #94a3b8;
        }

        .seo-input:disabled {
          cursor: not-allowed;
          background: #f1f5f9;
        }
      `}</style>
    </div>
  );
}

// ==========================================
// SECTION
// ==========================================

function Section({ title, children }) {
  return (
    <section>
      <h3 className="mb-4 border-b border-slate-200 pb-2 text-base sm:text-lg font-bold text-slate-900">
        {title}
      </h3>

      {children}
    </section>
  );
}

// ==========================================
// FIELD
// ==========================================

function Field({
  label,
  required = false,
  count,
  max,
  children,
}) {
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between gap-3">
        <label className="text-sm font-semibold text-slate-700">
          {label}

          {required && (
            <span className="ml-1 text-red-500">
              *
            </span>
          )}
        </label>

        {count !== undefined && (
          <span className="text-[11px] text-slate-400">
            {count}/{max}
          </span>
        )}
      </div>

      {children}
    </div>
  );
}

// ==========================================
// INFO BOX
// ==========================================

function InfoBox({ icon: Icon, text }) {
  return (
    <div className="mb-4 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />

      <p className="text-xs leading-5 text-blue-700">
        {text}
      </p>
    </div>
  );
}

// ==========================================
// IMAGE UPLOAD
// ==========================================

function ImageUpload({
  label,
  file,
  preview,
  onChange,
  onRemove,
}) {
  return (
    <div className="mt-4">
      <label className="mb-2 block text-sm font-semibold text-slate-700">
        {label}
      </label>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_240px]">

        {/* UPLOAD */}
        <label className="flex min-h-[170px] cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 px-4 text-center transition hover:border-[#F5A623] hover:bg-amber-50">

          <Upload className="mb-3 h-8 w-8 text-[#F5A623]" />

          <span className="text-sm text-slate-700">
            {file
              ? file.name
              : "Click to upload image"}
          </span>

          <span className="mt-2 text-xs text-slate-400">
            JPG, PNG, WEBP • Max 5MB
          </span>

          <span className="mt-1 text-xs text-slate-500">
            Recommended: 1200 × 630
          </span>

          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={onChange}
            className="hidden"
          />
        </label>

        {/* PREVIEW */}
        <div className="relative flex min-h-[170px] items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-slate-50">

          {preview ? (
            <>
              <img
                src={preview}
                alt={`${label} preview`}
                className="h-full min-h-[170px] w-full object-cover"
              />

              {/* REMOVE BUTTON */}
              <button
                type="button"
                onClick={onRemove}
                className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-red-600 text-white shadow-lg transition hover:bg-red-700"
                title={`Remove ${label}`}
              >
                <X className="h-4 w-4" />
              </button>
            </>
          ) : (
            <div className="px-4 text-center text-slate-400">
              <ImageIcon className="mx-auto mb-2 h-8 w-8" />

              <p className="text-xs">
                Default image will be used
              </p>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}